import { env } from "@/app/env";
import type { ApiErrorBody } from "@/api/types";

type TokenProvider = (forceRefresh?: boolean) => Promise<string>;

let tokenProvider: TokenProvider | null = null;

export function setTokenProvider(provider: TokenProvider) {
  tokenProvider = provider;
}

export class ApiError extends Error {
  status: number;
  code: string;
  requestId: string;

  constructor(status: number, code: string, message: string, requestId = "") {
    super(`${status} ${code}: ${message}`);
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

export async function apiRequest<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  if (!tokenProvider) {
    throw new ApiError(401, "token_provider_missing", "Token provider is not configured");
  }
  const token = await tokenProvider(retried);
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  headers.set("Authorization", `Bearer ${token}`);
  if (!(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(`${env.apiBaseUrl}${path}`, {
    ...init,
    headers
  });

  if (response.status === 401 && !retried) {
    return apiRequest<T>(path, init, true);
  }
  if (!response.ok) {
    throw await apiError(response);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

async function apiError(response: Response) {
  let body: ApiErrorBody = {};
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    body = {};
  }
  const error = body.error;
  return new ApiError(
    response.status,
    error?.code || "http_error",
    error?.message || response.statusText,
    error?.requestId || ""
  );
}
